import { useNavigate } from "react-router-dom";
import { InquiryForm } from "@/components/inquiry/inquiry-form";
import { InquiryFormData, TourPackageItem } from "@/types/database";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";

export function InquiryAdd() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Fetch tour packages for name lookup
  const { data: tourPackages = [] } = useQuery<TourPackageItem[]>({
    queryKey: ["tourPackages"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tour_package")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });

  // Handle refresh button click - reload the entire page
  const handleRefresh = () => {
    window.location.reload();
  };

  const handleSubmit = async (data: InquiryFormData) => {
    try {
      // Check if user is logged in
      if (!user) {
        toast.error("You must be logged in to create an inquiry");
        return;
      }
      
      // Generate voucher code with new format "query" + 5 digits
      const randomDigits = Math.floor(Math.random() * 100000).toString().padStart(5, '0');
      const voucherCode = `query${randomDigits}`;

      // Extract main inquiry data
      const inquiryData = {
        name: data.guest_name,
        date: data.start_date,  // Keep date for database compatibility
        start_date: data.start_date,
        adult_count: data.adult_count,
        child_count: data.child_count,
        nights: data.nights,
        status: data.status,
        hotel_type: data.hotel_type,
        car_type: data.car_type,
        remarks: data.remarks,
        flight_information: data.flight_information,
        user_id: user.id, // Add the user_id from the authenticated user
        voucher_code: voucherCode,

        // JSON data
        tour_package_json: data.tour_package_json,
        hotel_json: data.hotel_json,

        // Totals
        total_transport_cost: data.total_transport_cost,
        total_parking_cost: data.total_parking_cost,
        total_adult_entrance_fee: data.total_adult_entrance_fee,
        total_child_entrance_fee: data.total_child_entrance_fee,
        total_entrance_fee: data.total_entrance_fee,
        total_meal_cost: data.total_meal_cost,
        total_other_cost: data.total_other_cost,
        total_hotel_price: data.total_hotel_price,
        grand_total: data.grand_total,
        total_usd: data.total_usd
      };

      // Insert main inquiry
      const { error } = await supabase
        .from("inquiry")
        .insert(inquiryData);

      if (error) throw error;

      // Force invalidate the query cache
      await queryClient.invalidateQueries({ queryKey: ["inquiry"] });
      
      // Set a flag in sessionStorage to indicate that we need to refresh
      // This will be checked on the Inquiry page
      sessionStorage.setItem("shouldRefreshInquiry", "true");
      
      toast.success("Inquiry created successfully");
      
      // Navigate back to inquiry page
      navigate("/inquiry");
    } catch (error) {
      console.error("Error creating inquiry:", error);
      toast.error("Failed to create inquiry");
    }
  };

  return (
    <div className="container mx-auto py-10">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/inquiry")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-3xl font-bold">New Inquiry</h1>
        <div className="ml-auto">
          <Button 
            variant="outline" 
            onClick={handleRefresh}
            className="bg-green-500 hover:bg-green-600 text-white border-green-500 hover:border-green-600"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="">
        <InquiryForm onSubmit={handleSubmit} />
      </div>
    </div>
  );
}
